import { useEffect, useState } from "react";
import { loadSession } from "../lib/session";
import { InlineSpinner } from "./InlineSpinner";

/**
 * 노래 앨범 그림 — 곡을 만들 때 서버가 같이 그린 그림 한 장(2026-10-14 천상욱 강사님 제안 · jery 승인).
 *
 * 🔑 「작곡가 ○○○」 는 AI 가 그리지 않는다 — AI 가 그리는 한글은 깨진다. 화면이 그림 위에 얹는다(A안 띠).
 * 🔑 그림은 `/api/jobs/:id/cover` 가 같은 주소로 중계한다 — R2 에 CORS 가 없어서 서명 URL 로 받으면
 *    canvas 로 이름을 얹어 저장할 수 없다.
 * 🔑 그림이 없으면(옛 곡 · 실패 · 서버가 아직 모름) 아무것도 그리지 않는다 — 곡이 주인공이다.
 */

const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";
// 🔑 2026-10-15 jery: 2.5초 → 5초. 학생 요청 상한이 분당 36건 → 24건으로 내려가 R11 수용이 오른다.
const POLL_MS = 5_000;
/** 그림은 보통 20초 안에 끝난다. 5분(5초 × 60회)이 지나도 «그리는 중» 이면 포기하고 칸을 접는다. */
const MAX_POLLS = 60;

export type SongCoverStatus = "loading" | "pending" | "ready" | "none";

/**
 * @param waitWhileMissing 곡을 «만드는 중» 일 때만 참. 곡 잡이 아직 줄에서 기다리면 서버가 그림을
 *   잡기 전이라 404 가 온다 — 그때 접어 버리면 그림이 다 그려져도 영영 안 보인다.
 */
export function useSongCover(workId: string, jobId: string | null | undefined, waitWhileMissing = false): {
  status: SongCoverStatus;
  url: string | null;
  blob: Blob | null;
} {
  const [status, setStatus] = useState<SongCoverStatus>(jobId ? "loading" : "none");
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      setStatus("none");
      return undefined;
    }
    let active = true;
    let timer: number | undefined;
    let polls = 0;
    let errors = 0;
    let objectUrl: string | null = null;
    setStatus("loading");
    setBlob(null);
    setUrl(null);

    const tick = async (): Promise<void> => {
      polls += 1;
      const token = loadSession(workId)?.token;
      if (!token) {
        if (active) setStatus("none");
        return;
      }
      try {
        const response = await fetch(`${apiBase}/api/jobs/${encodeURIComponent(jobId)}/cover`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (!active) return;
        if (response.status === 200) {
          const image = await response.blob();
          if (!active) return;
          objectUrl = URL.createObjectURL(image);
          setBlob(image);
          setUrl(objectUrl);
          setStatus("ready");
          return;
        }
        if (response.status === 202) {
          setStatus("pending");
        } else if (response.status === 404 && waitWhileMissing) {
          // 아직 그림을 잡기 전 — 계속 기다린다.
        } else if (response.status === 404 || response.status === 403 || response.status === 401) {
          setStatus("none");
          return;
        } else {
          errors += 1;
          if (errors >= 4) {
            setStatus("none");
            return;
          }
        }
      } catch {
        errors += 1;
        if (errors >= 4) {
          if (active) setStatus("none");
          return;
        }
      }
      if (active && polls < MAX_POLLS) {
        timer = window.setTimeout(() => void tick(), POLL_MS);
      } else if (active) {
        setStatus("none");
      }
    };
    void tick();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [jobId, workId, waitWhileMissing]);

  return { status, url, blob };
}

function safeFileName(raw: string): string {
  return raw.replace(/[\\/:*?"<>|\u0000-\u001f]/gu, " ").replace(/\s+/gu, " ").trim().slice(0, 80) || "노래 그림";
}

/** 그림 + A안 띠(곡 제목 · 작곡가)를 한 장으로 구워 내려받는다. */
export async function saveSongCover(blob: Blob, title: string, composer: string): Promise<void> {
  const bitmap = await createImageBitmap(blob);
  const size = Math.max(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("canvas unavailable");
  context.drawImage(bitmap, 0, 0);
  const bandTop = canvas.height * 0.66;
  const gradient = context.createLinearGradient(0, bandTop, 0, canvas.height);
  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(1, "rgba(0,0,0,0.62)");
  context.fillStyle = gradient;
  context.fillRect(0, bandTop, canvas.width, canvas.height - bandTop);
  await document.fonts?.ready;
  const family = "\"Pretendard Variable\", Pretendard, system-ui, sans-serif";
  const pad = size * 0.05;
  context.fillStyle = "#ffffff";
  context.shadowColor = "rgba(0,0,0,0.4)";
  context.shadowBlur = size * 0.008;
  context.textBaseline = "alphabetic";
  const byY = canvas.height - pad;
  const titleY = byY - size * 0.058;
  if (title) {
    context.font = `800 ${Math.round(size * 0.055)}px ${family}`;
    context.fillText(title, pad, titleY, canvas.width - pad * 2);
  }
  context.font = `500 ${Math.round(size * 0.04)}px ${family}`;
  const label = "작곡가 ";
  context.fillText(label, pad, byY);
  const nameX = pad + context.measureText(label).width + size * 0.004;
  context.font = `800 ${Math.round(size * 0.04)}px ${family}`;
  // 긴 이름(최대 30자)도 그림 밖으로 나가지 않게 남은 폭 안으로 줄인다(Codex 검수 P2).
  context.fillText(composer, nameX, byY, Math.max(1, canvas.width - pad - nameX));
  const out = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => (value ? resolve(value) : reject(new Error("toBlob"))), "image/png");
  });
  const href = URL.createObjectURL(out);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = `${safeFileName(`${title || "내 노래"} - 작곡가 ${composer}`)}.png`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

export function SongCover({
  workId,
  jobId,
  title,
  showSave = false,
  generating = false,
}: {
  workId: string;
  jobId: string | null | undefined;
  /** 곡 제목 — 아직 곡이 없으면(만드는 중) 비워 둔다. */
  title?: string;
  showSave?: boolean;
  /** 곡을 만드는 중이면 참 — 그림을 아직 잡기 전(404)이어도 기다린다. */
  generating?: boolean;
}) {
  const { status, url, blob } = useSongCover(workId, jobId, generating);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const composer = loadSession(workId)?.name?.trim() || "나";

  if (status === "none") return null;
  if (status !== "ready" || !url) {
    // loading 은 아직 서버 대답 전 — 그림이 있을지 모르므로 자리만 잡고 문구는 «그리는 중» 으로 둔다.
    return (
      <div className="song-cover song-cover-pending" role="status" aria-label="노래 그림 그리는 중">
        <span>🎨 노래에 어울리는 그림을 그리는 중...</span>
      </div>
    );
  }

  const save = async (): Promise<void> => {
    if (!blob) return;
    setSaving(true);
    setSaveError(false);
    try {
      await saveSongCover(blob, title ?? "", composer);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="song-cover-wrap">
      <figure className="song-cover">
        <img src={url} alt={`${title ? `「${title}」 ` : ""}노래 그림`} />
        <figcaption className="song-cover-band">
          {title ? <strong className="song-cover-title">{title}</strong> : null}
          <span className="song-cover-by">작곡가 <b>{composer}</b></span>
        </figcaption>
      </figure>
      {showSave ? (
        <button className="button-secondary full-width" type="button" onClick={() => void save()} disabled={saving}>
          {saving ? <InlineSpinner label="그림 저장 준비 중..." /> : "그림 저장 (이름 들어간 그대로)"}
        </button>
      ) : null}
      {saveError ? <p className="track-error">그림을 저장하지 못했어요. 다시 눌러 주세요.</p> : null}
    </div>
  );
}
